import React, { useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, GlassButton } from 'dashboard/components/common';
import ResourceDialog from './ResourceDialog';

/**
 * The things people can book.
 *
 * Shown as a summary rather than a table: what matters at a glance is how long
 * a booking is, how many fit, and which days it is open — three facts that
 * read better as a sentence than as columns a quarter full.
 */

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const openDays = (resource) => {
  const open = new Set((resource.opening_hours || []).map((h) => h.weekday));
  return [1, 2, 3, 4, 5, 6, 0].map((d) => ({ d, open: open.has(d) }));
};

export default function ResourcesTab({ brandId, resources, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const remove = (resource) => {
    if (!window.confirm(`Remove "${resource.name}"?`)) return;
    API.deleteBookableResource({ brand_id: brandId, _id: resource._id })
      .then((res) => {
        // Switched off rather than deleted when people hold bookings on it —
        // say so, or it looks like the delete silently failed.
        toast.success(res?.deactivated
          ? `Switched off — ${res.upcoming} booking(s) still to come, so it was kept.`
          : 'Removed');
        onChanged();
      })
      .catch((err) => toast.error(err?.message || 'Could not remove it'));
  };

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb='14px' gap='10px' flexWrap='wrap'>
        <Typography variant='body2' color='text.secondary'>
          A table, a chair, a room, a court — anything with opening hours and a number of places.
        </Typography>
        <GlassButton
          variant='contained' startIcon={<AddIcon />}
          onClick={() => { setEditing(null); setOpen(true); }}
          disabled={!brandId}
          disabledReason='Choose a brand at the top of the sidebar first.'
        >
          Add
        </GlassButton>
      </Stack>

      {resources.length === 0 ? (
        <EmptyState
          action={(
            <GlassButton variant='contained' startIcon={<AddIcon />} onClick={() => { setEditing(null); setOpen(true); }}>
              Add the first one
            </GlassButton>
          )}
        >
          Nothing can be booked yet. Add a table, a chair or a room and people can start booking it.
        </EmptyState>
      ) : (
        <Stack spacing='8px'>
          {resources.map((r) => (
            <Stack
              key={r._id}
              direction='row' alignItems='center' gap='12px' flexWrap='wrap'
              sx={{
                p: '12px 14px', border: '1px solid', borderColor: 'divider',
                borderRadius: '12px', opacity: r.is_active === false ? 0.55 : 1,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction='row' alignItems='center' gap='8px' flexWrap='wrap'>
                  <Typography fontWeight='600' noWrap>{r.name}</Typography>
                  {r.is_active === false && <Chip size='small' label='Off' />}
                  {r.auto_confirm === false && <Chip size='small' variant='outlined' label='You confirm' />}
                </Stack>
                <Typography variant='caption' color='text.secondary' display='block'>
                  {r.duration_minutes} min · {r.capacity} {r.capacity === 1 ? 'place' : 'places'}
                  {r.description ? ` · ${r.description}` : ''}
                </Typography>
              </Box>

              <Stack direction='row' gap='3px' aria-label='Open days'>
                {openDays(r).map(({ d, open: isOpen }, i) => (
                  <Box
                    key={`${d}-${i}`}
                    sx={{
                      width: 20, height: 20, borderRadius: '6px',
                      display: 'grid', placeItems: 'center',
                      fontSize: 11, fontWeight: 700,
                      bgcolor: isOpen ? 'primary.main' : 'action.hover',
                      color: isOpen ? 'primary.contrastText' : 'text.disabled',
                    }}
                  >
                    {DAY_LETTERS[d]}
                  </Box>
                ))}
              </Stack>

              <Stack direction='row' gap='6px'>
                <GlassButton size='small' onClick={() => { setEditing(r); setOpen(true); }}>Edit</GlassButton>
                <GlassButton size='small' color='warning' onClick={() => remove(r)}>Remove</GlassButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <ResourceDialog
        open={open}
        onClose={() => setOpen(false)}
        brandId={brandId}
        resource={editing}
        onSaved={onChanged}
      />
    </Box>
  );
}
